import { useEffect, useState } from "react";

import { Tooltip, IconButton } from "@mui/material";
import Box from "@mui/material/Box";

import SettingsIcon from "@mui/icons-material/Settings";
import "@plannel/scss/components/HrchyModal.scss";
import ClearLayoutIcon from "@mui/icons-material/UnpublishedOutlined";
// 헤더 표식과 이 패널을 여는 툴바 버튼은 같은 아이콘을 쓴다 — 누른 버튼과 열린 패널이 이어져 보이게.
// (툴바 쪽: Workbench / RpReview / PsiSimulation 세 화면)
import ViewComfySharpIcon from "@mui/icons-material/ViewComfySharp";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";

import SidePanel, {
  PANEL,
  PANEL_HEAD_ICON_SX,
  PanelBadge,
  PanelRow,
  PanelSearchField,
} from "@plannel/components/ui/SidePanel";

import p13nService from "@plannel/services/system/p13n-service";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";

import Snackbar from "@plannel/components/Snackbar";
import { withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";
import {isEqual, size, isEmpty, cloneDeep, includes} from "lodash";
import LayoutManagementModal from "./LayoutManagementModal";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";

const LayoutListModal = props => {
  const { close, open, t, pageCd, activeMeasure, reduxViewState, modalResultData, displayNames = null } = props;

  const DP_WORKBENCH = "DP_WORKBENCH";
  const CHANNEL_WORKBENCH = "DP_CHANNEL_WORKBENCH";
  const DEFAULT_LAYOUT = "DEFAULT";
  const isDp = isEqual(pageCd, DP_WORKBENCH) || isEqual(pageCd, CHANNEL_WORKBENCH);
  const [customerData, setCustomerData] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [searchWord, setSearchWord] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const [layoutData, setLayoutData] = useState([]);
  const [defaultLayoutId, setDefaultLayoutId] = useState(null);
  const [layoutManageOpen, setLayoutManageOpen] = useState(false);
  const [layoutSelected, setLayoutSelected] = useState(reduxViewState?.layoutId ? { id: reduxViewState?.layoutId } :
    reduxViewState?.layoutModalData?.layoutId ? { id: reduxViewState?.layoutModalData?.layoutId } : {});
  const [appliedLayout, setAppliedLayout] = useState(reduxViewState?.appliedLayout ?? null);

  const hasAppliedLayout = size(layoutSelected) > 0;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  /*
   * 행 아래 회색 한 줄 — 레이아웃이 실제로 들고 있는 값만 쓴다(없으면 그 조각을 뺀다).
   * 시안의 "Weekly buckets · 8 measures" 자리인데, 이 화면 데이터에 버킷·measure 수가 없어
   * 대신 저장된 조회 레벨과 공유 여부를 보여준다. 없는 값을 지어내지 않는다.
   */
  const getLayoutMeta = layout => [
    layout?.selectCL?.name,
    layout?.selectIL?.name,
    layout?.shared ? t("shared") : null,
  ].filter(Boolean).join(" · ");

  const {
    snackBarMessageColor: { saveSuccess, saveFail },
  } = ColorUtil;

  /*
   * 목록에 실제로 보일 레이아웃만 남긴다.
   *
   * `DEFAULT` 행은 레이아웃이 아니라 **어느 레이아웃이 기본인가**를 가리키는 포인터라
   * (layoutId: "DEFAULT", layout: 대상 layoutId) 목록에서 뺀다.
   *
   * 예전에는 여기서 기본 레이아웃을 맨 위로 올리기까지 했다. 그런데 별을 누르면 저장 후
   * 다시 조회하므로, 목록 다섯째 줄의 별을 누른 사용자 눈에는 그 자리에 **다른 레이아웃**이
   * 빈 별을 달고 올라온다 — "저장은 됐다는데 별이 안 바뀐다"는 신고가 이것이었다.
   * 순서는 서버가 준 대로 둔다.
   */
  const visibleLayouts = list => list.filter(lay => lay.layoutId !== DEFAULT_LAYOUT);

  const retrieveP13n = () => {
    const p13PageCd = isDp ? pageCd : `${pageCd}_LAYOUT`;
    p13nService.getLayout(p13PageCd)
      .then(response => {
        const { data } = response;
        if (data) {
          const defaultLayout = data?.find(ly => ly.layoutId === DEFAULT_LAYOUT);
          setDefaultLayoutId(defaultLayout?.layout ?? null);
          setLayoutData(visibleLayouts(data));
          setFilteredData(filterBy(visibleLayouts(data), searchWord));
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveCustomerHrchies = () => {
    setCustomerData([]);
    customerHrchyService.getAll({ searchFilters: { isAll: true } })
      .then(response => {
        const dataResults = response.data.results;
        setCustomerData(dataResults);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveItemHrchies = () => {
    setItemData([]);
    itemHrchyService.getAll()
      .then(response => {
        if (response.data) {
          setItemData(response.data.results);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const openManagment = () => {
    setLayoutManageOpen(true);
  };

  const layoutModalClose = () => {
    setLayoutManageOpen(false);
    retrieveP13n();
  };

  const handleClose = () => {
    close(false);
  };

  const saveDefault = (layout, isDefault) => {
    const layoutDto = [];
    const sharedLayoutDto = [];
    let defaultLayout = null;
    if (isDefault) {
      defaultLayout = {
        layoutId : DEFAULT_LAYOUT,
        layout: layout.layoutId
      };
    } else {
      defaultLayout = {
        layoutId : DEFAULT_LAYOUT,
        layout: null
      }
    }
    layoutDto.push(defaultLayout);

    const oriChangeDatas = cloneDeep(layoutData);
    const changeDatas = oriChangeDatas.filter(
      ly => ly?.layoutId !== null && ly?.layoutId !== undefined
    );

    changeDatas.forEach(ly => {
      ly.shared ? sharedLayoutDto.push(ly) : layoutDto.push(ly);
    });

    const p13PageCd = isDp ? pageCd : `${pageCd}_LAYOUT`;

    const common = "_COMMON";
    const p13nDto = {
      id: layoutDto?.[0]?.p13nId,
      user: null,
      pageCd: p13PageCd,
      layoutDtos: layoutDto,
      activeFlg: false,
    };

    const p13nSharedDto = {
      pageCd: `${p13PageCd}${common}`,
      layoutDtos: sharedLayoutDto,
      activeFlg: true,
    };

    p13nService
      .upsertLayout([p13nDto, p13nSharedDto])
      .then(() => {
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        retrieveP13n();
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      });
  };

  const applyLayout = e => {
    if (layoutData.length > 0) {
      const selectedLayoutData = layoutData.filter(lay => isEqual(lay.layoutId, e.id));
        if (!isEmpty(selectedLayoutData)) {
          const layout = selectedLayoutData[0];
          if (isDp) {
            const custHrchyList = [];
            const itemHrchyList = [];
            if (!isEmpty(layout.custFilter)) {
              for (const hrchy of customerData) {
                for (const filter of layout.custFilter) {
                  if (isEqual(filter, hrchy.hrchyCd)) {
                    custHrchyList.push(hrchy);
                  }
                }
              }
            } else {
              for (const hrchy of customerData) {
                if (isEqual(hrchy.hrchyLv, layout.selectCL.cd)) {
                  custHrchyList.push(hrchy);
                }
              }
            }
            if (!isEmpty(layout.itemFilter)) {
              for (const hrchy of itemData) {
                for (const filter of layout.itemFilter) {
                  if (isEqual(filter, hrchy.hrchyCd)) {
                    itemHrchyList.push(hrchy);
                  }
                }
              }
            } else {
              for (const hrchy of itemData) {
                if (isEqual(hrchy.hrchyLv, layout.selectIL.cd)) {
                  itemHrchyList.push(hrchy);
                }
              }
            }
            layout.custHrchyList = custHrchyList;
            layout.itemHrchyList = itemHrchyList;
            layout.custLevelCd = layout?.selectCL?.cd;
            layout.itemLevelCd = layout?.selectIL?.cd;
          } else {
            const obj = itemData.filter(hrchy => includes(layout.itemHrchy, hrchy.hrchyCd));
            if (obj.length > 0) {
              layout.itemHrchyObj = obj;
            }
          }
          modalResultData({ ...layout, reload: true });
          setLayoutSelected({ id: layout.layoutId });
        }
    }
    close(false);
  };

  const clearLayout = () => {
    if (size(layoutSelected) > 0) {
      modalResultData({ clearLayout: true });
      setLayoutSelected("");
      handleClose();
    }
  };
  
  const filterBy = (arr, query) => {
  if (!query || isEmpty(query)) {
    return arr;
  }

    return query ? arr.filter(item => item.layout?.toLowerCase().includes(query.toLowerCase())) : arr;
  };

  const onChange = e => {
    const value = e.target.value;
    setSearchWord(value);
    setFilteredData(filterBy(layoutData, value));
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      onChange(e);
    }
  };

  useEffect(() => {
    if (reduxUtil.getViewState("ITEM_HRCHY")?.itemHrchy) {
      setItemData(reduxUtil.getViewState("ITEM_HRCHY")?.itemHrchy);
    } else {
      retrieveItemHrchies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      retrieveP13n();
      retrieveCustomerHrchies();
      if (reduxViewState?.layoutId || (reduxViewState?.layoutModalData?.layoutId)) {
        setLayoutSelected({ id: reduxViewState?.layoutId ?? reduxViewState?.layoutModalData?.layoutId });
      } else {
        setLayoutSelected("");
        setAppliedLayout(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (appliedLayout) {
      applyLayout(appliedLayout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedLayout]);

  return (
    <>
      {/*
        * 다른 두 패널(Note / Advanced Filter)과 같이 **본문 옆에 자리를 차지한다**.
        *   예전에는 Dialog + position:absolute 로 그리드 위에 떠 있어서, 셋을 같이 열면
        *   이것만 다른 패널을 덮었다. 자리 배치는 호출부(Workbench / RpReview / PsiSimulation)가 한다.
        */}
      {open ? (
        <SidePanel
          id="layout-list-modal"
          resizable
          icon={<ViewComfySharpIcon />}
          title={t("dpUserLayouts")}
          subtitle={[
            `${t("layout")} ${size(filteredData)}`,
            layoutData.find(lay => isEqual(lay.layoutId, layoutSelected?.id))?.layout,
          ].filter(Boolean).join(" · ")}
          actions={
            <>
              {/*
               * 초기화는 헤더에 둔다 — 목록 아래에 있으면 레이아웃이 많은 사용자에게는
               * 스크롤 밖으로 밀려 아예 안 보인다. 파괴적 동작이라 비활성 상태도 분명히 드러낸다.
               * (disabled 버튼은 이벤트를 내지 않아 Tooltip 이 못 붙는다 — span 으로 감싼다.)
               */}
              <Tooltip title={t("clearLayout")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!hasAppliedLayout}
                    onClick={clearLayout}
                    sx={PANEL_HEAD_ICON_SX}>
                    <ClearLayoutIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("management")}>
                <IconButton size="small" onClick={openManagment} sx={PANEL_HEAD_ICON_SX}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </>
          }
          onClose={handleClose}
          toolbar={
            <PanelSearchField
              value={searchWord}
              onChange={onChange}
              onKeyPress={handleKeyPress}
              placeholder={t("search")}
              sx={{ flex: 1 }}
            />
          }
          bodySx={{ gap: "12px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "9px" }}>
            {filteredData?.map(layout => {
              const isDefault = isEqual(layout?.layoutId, defaultLayoutId);
              const isApplied = isEqual(layoutSelected?.id, layout?.layoutId);
              return (
                <PanelRow
                  key={layout?.layoutId}
                  active={isApplied}
                  onClick={() => applyLayout({ id: layout?.layoutId, name: layout?.layout })}
                  title={layout?.layout}
                  meta={getLayoutMeta(layout)}
                  leading={
                    /*
                     * 별은 "기본 레이아웃" 토글이라 행 전체의 적용(onClick)과 별개 동작이다.
                     * 클릭이 행으로 번지면 별을 누를 때마다 레이아웃이 적용되고 패널이 닫힌다.
                     */
                    <Tooltip title={t("default")}>
                      <Box
                        component="span"
                        onClick={e => {
                          e.stopPropagation();
                          saveDefault(layout, !isDefault);
                        }}
                        sx={{ display: "flex", flex: "none", cursor: "pointer", "& > svg": { fontSize: 20 } }}>
                        {isDefault ? (
                          <StarRateRoundedIcon sx={{ color: PANEL.star }} />
                        ) : (
                          <StarBorderRoundedIcon sx={{ color: PANEL.iconFaint }} />
                        )}
                      </Box>
                    </Tooltip>
                  }
                  trailing={isApplied ? <PanelBadge>{t("apply")}</PanelBadge> : null}
                />
              );
            })}
          </Box>
        </SidePanel>
      ) : null}
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <LayoutManagementModal
        open={layoutManageOpen}
        close={layoutModalClose}
        pageCd={pageCd}
        displayModButtons
        activeMeasures={activeMeasure}
        displayNames={displayNames}
        pageReduxViewState={reduxViewState}
      />
    </>
  );
};

export default withTranslation()(LayoutListModal);
